import { View, Text, styled, ScrollView, useTheme } from 'tamagui'
import { Hash, Volume2, Plus, Trash2 } from '@tamagui/lucide-icons'
import { useState } from 'react'
import { useAppStore, useServerStore } from '@/store'

const Container = styled(View, { flex: 1, flexDirection: 'column' })

const Header = styled(View, {
  paddingHorizontal: 32, paddingVertical: 20,
  borderBottomWidth: 1, borderBottomColor: '$borderDefault',
  flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
})

const Title = styled(Text, {
  fontFamily: '$fontHeading', fontWeight: '600', fontSize: 20, color: '$surfaceForeground',
})

const Body = styled(View, { padding: 32, gap: 8, maxWidth: 600 })

const ChannelRow = styled(View, {
  flexDirection: 'row', alignItems: 'center', height: 40, paddingHorizontal: 12,
  borderRadius: '$borderRadiusMd', gap: 8, borderWidth: 1, borderColor: '$borderDefault',
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const ChannelName = styled(Text, {
  fontFamily: '$fontBody', fontSize: 14, color: '$surfaceForeground', flex: 1,
})

const AddButton = styled(View, {
  height: 36, paddingHorizontal: 16, borderRadius: '$borderRadiusMd',
  alignItems: 'center', justifyContent: 'center', backgroundColor: '$accent',
  cursor: 'pointer', flexDirection: 'row', gap: 6,
  hoverStyle: { opacity: 0.9 },
})

const DeleteButton = styled(View, {
  width: 28, height: 28, borderRadius: '$borderRadiusMd', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', hoverStyle: { backgroundColor: 'rgba(207, 62, 79, 0.15)' },
})

const CategoryLabel = styled(Text, {
  fontFamily: '$fontBody', fontSize: 11, fontWeight: '600',
  color: '$color8', textTransform: 'uppercase', letterSpacing: 0.5,
  paddingTop: 12, paddingBottom: 4,
})

export function ChannelSettings() {
  const { activeServerId } = useAppStore()
  const server = useServerStore((s) => activeServerId ? s.servers[activeServerId] : undefined)
  const { addChannel, removeChannel } = useServerStore()
  const theme = useTheme()
  const [newName, setNewName] = useState('')
  const [showAdd, setShowAdd] = useState(false)

  if (!server || !activeServerId) return null

  const handleAdd = () => {
    if (!newName.trim()) return
    const catId = server.categories[0]?.id || 'default'
    addChannel(activeServerId, {
      id: `ch-${Date.now()}`,
      name: newName.trim().toLowerCase().replace(/\s+/g, '-'),
      type: 'text',
      categoryId: catId,
      topic: '',
      unreadCount: 0,
      mentionCount: 0,
    })
    setNewName('')
    setShowAdd(false)
  }

  return (
    <Container>
      <Header>
        <Title>Channels</Title>
        <AddButton onPress={() => setShowAdd(!showAdd)}>
          <Plus size={14} color="$surface" />
          <Text fontFamily="$fontBody" fontWeight="600" fontSize={13} color="$surface">Add Channel</Text>
        </AddButton>
      </Header>
      <ScrollView flex={1}>
        <Body>
          {showAdd && (
            <View flexDirection="row" gap={8} marginBottom={8}>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="channel-name"
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                style={{
                  background: 'transparent',
                  border: `1px solid ${theme.borderDefault?.val || '#2A3142'}`,
                  borderRadius: 6, padding: '6px 12px', flex: 1,
                  color: theme.surfaceForeground?.val || '#E2E8F0',
                  fontFamily: '$fontBody', fontSize: 14, outline: 'none',
                }}
              />
              <AddButton onPress={handleAdd}>
                <Text fontFamily="$fontBody" fontWeight="600" fontSize={13} color="$surface">Create</Text>
              </AddButton>
            </View>
          )}

          {server.categories.map((cat) => {
            const channels = server.channels.filter(c => c.categoryId === cat.id)
            return (
              <View key={cat.id}>
                <CategoryLabel>{cat.name}</CategoryLabel>
                {channels.map((ch) => (
                  <ChannelRow key={ch.id}>
                    {ch.type === 'voice'
                      ? <Volume2 size={16} color="$warning" />
                      : <Hash size={16} color="$color8" />
                    }
                    <ChannelName>{ch.name}</ChannelName>
                    <DeleteButton onPress={() => removeChannel(activeServerId, ch.id)}>
                      <Trash2 size={14} color="$error" />
                    </DeleteButton>
                  </ChannelRow>
                ))}
              </View>
            )
          })}
        </Body>
      </ScrollView>
    </Container>
  )
}
